import "./EditorModal.scss";
import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { useAppSelector } from "../../../../store/hooks";
import {
  TEMPLATE_USE_GENERAL,
  normalizeTemplateUseValue,
  type Template,
} from "../../../../store/reducers/builderReducers/templates/templateApi";
import type { AllComponents } from "../../../_default/interfaces/base";
import { getComponentFromStructureMap } from "../../../_default/utilities/componentIdentity";
import {
  cloneComponentForSave,
  buildComponentsMapForSave,
} from "../../../_default/utilities/templateStructureForSave";
import TemplateDetailsGeneralEditForm from "../../Sidebar/SubSidebar/Templates/TemplateDetailsGeneralEditForm";

interface Props {
  createComponentTemplate: AllComponents;
  setCreateComponentTemplate: React.Dispatch<
    React.SetStateAction<AllComponents | null>
  >;
  template: Template | undefined;
}

export default function CreateTemplateModal({
  createComponentTemplate,
  setCreateComponentTemplate,
  template: existingTemplate,
}: Props) {
  const structureComponents = useAppSelector(
    (state) => state.builder.structure.components
  );

  const formTemplate = useMemo((): Template => {
    const component = createComponentTemplate;
    const base: Template = {
      name: "",
      use: TEMPLATE_USE_GENERAL,
      component,
      languageCode: null,
      visible: true,
      webpages: [],
    };

    const rootUuid = existingTemplate?.rootComponentUuid?.trim() ?? "";
    if (!rootUuid || !existingTemplate) {
      return base;
    }

    const merged: Template = {
      ...existingTemplate,
      rootComponentUuid: rootUuid,
      component,
      name:
        typeof existingTemplate.name === "string" ? existingTemplate.name : "",
      languageCode: existingTemplate.languageCode ?? null,
      visible:
        typeof existingTemplate.visible === "boolean"
          ? existingTemplate.visible
          : true,
      webpages: Array.isArray(existingTemplate.webpages)
        ? existingTemplate.webpages
        : [],
      use: normalizeTemplateUseValue(existingTemplate.use),
    };

    const rootLive = getComponentFromStructureMap(structureComponents, rootUuid);
    if (!rootLive) {
      return merged;
    }

    const savedTree = cloneComponentForSave(
      rootLive,
      structureComponents ?? {},
      new Set<string>()
    );
    const savedComponents = buildComponentsMapForSave(savedTree);
    return {
      ...merged,
      component: savedTree,
      components: savedComponents,
      rootComponentUuid: savedTree.uuid?.trim() || rootUuid,
    };
  }, [createComponentTemplate, existingTemplate, structureComponents]);

  const closeModal = () => setCreateComponentTemplate(null);

  return createPortal(
    <div
      className="editor-modal-backdrop"
      onClick={closeModal}
      role="presentation"
    >
      <div
        className="editor-modal editor-modal__form editor-modal--portal-panel"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-template-modal-title"
      >
        <div
          id="create-template-modal-title"
          className="container p-0"
          style={{
            fontSize: "14px",
            fontWeight: "normal",
            fontFamily: "sans-serif",
          }}
        >
          <TemplateDetailsGeneralEditForm
            setGeneralEdit={(v) => {
              if (v === false) closeModal();
            }}
            template={formTemplate}
            setEditTemplate={() => void 0}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
